import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { sanitizeSearchInput, toValidObjectIdString, formatApiDate } from '../utils/security';
import {
  fetchSiparisler,
  fetchSiparisDetay,
  fetchSiparisSekmeSayilari,
  iptalSiparis,
  siparisKargoGir,
  siparisFaturaGir,
} from '../services/siparisService';
import SiparisDetayModal from '../components/SiparisDetayModal';
import SiparisOnaylaModal from '../components/SiparisOnaylaModal';
import KargoFirmasiField from '../components/KargoFirmasiField';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

import {
  getSiparisTipLabel,
  formatSiparisListeFiyat,
} from '../utils/siparisDisplayHelpers';

const ALL_TABS = [
  { id: 'onay-bekleyen', label: 'Onay Bekleyen' },
  { id: 'kargo-bekleyen', label: 'Kargo Bekleyen' },
  { id: 'kargoda', label: 'Kargoda' },
  { id: 'fatura-bekleyen', label: 'Fatura Bekleyen' },
  { id: 'konsinye', label: 'Konsinye' },
  { id: 'tamamlanan', label: 'Tamamlanan' },
];

const CARGO_ONLY_TAB_IDS = ['kargo-bekleyen', 'kargoda', 'tamamlanan'];

const getSiparisId = (s) => toValidObjectIdString(s?.id ?? s?._id) || s?.siparisNumarasi || '';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const SiparisTakipPage = ({ cargoOnly = false }) => {
  const { confirm } = useConfirmDialog();
  const TABS = cargoOnly
    ? ALL_TABS.filter((t) => CARGO_ONLY_TAB_IDS.includes(t.id))
    : ALL_TABS;
  const TAB_IDS = TABS.map((t) => t.id);
  const defaultTab = cargoOnly ? 'kargo-bekleyen' : 'onay-bekleyen';

  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const tabFromUrl = searchParams.get('tab');
  const initialTab = TAB_IDS.includes(tabFromUrl) ? tabFromUrl : defaultTab;
  const aramaFromUrl = searchParams.get('arama') || '';

  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [filters, setFilters] = useState({ search: aramaFromUrl, tab: initialTab });
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [kargoModal, setKargoModal] = useState({ open: false, siparis: null });
  const [kargoForm, setKargoForm] = useState({ kargoFirmasi: '', kargoTakipNo: '' });
  const [onayModal, setOnayModal] = useState({ open: false, siparis: null });
  const [faturaModal, setFaturaModal] = useState({ open: false, siparis: null });
  const [faturaBilgisi, setFaturaBilgisi] = useState('');
  const [sekmeSayilari, setSekmeSayilari] = useState(null);

  const sanitizedSearch = useMemo(
    () => (filters.search && filters.search.trim() ? filters.search.trim() : ''),
    [filters.search]
  );

  const loadSiparisler = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchSiparisler({
        tab: filters.tab,
        arama: sanitizedSearch || undefined,
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
      });
      if (response?.basarili && response?.veri) {
        setSiparisler(response.veri.siparisler || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Siparişler yüklenemedi'));
      setSiparisler([]);
    } finally {
      setLoading(false);
    }
  }, [filters.tab, sanitizedSearch, currentPage]);

  const refreshSekmeSayilari = useCallback(async () => {
    try {
      const response = await fetchSiparisSekmeSayilari();
      if (response?.basarili && response?.veri) {
        setSekmeSayilari(response.veri);
      }
    } catch {
      setSekmeSayilari(null);
    }
  }, []);

  useEffect(() => {
    loadSiparisler();
  }, [loadSiparisler]);

  useEffect(() => {
    refreshSekmeSayilari();
  }, [filters.tab, refreshSekmeSayilari]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    if (!tabFromUrl || !TAB_IDS.includes(tabFromUrl)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', initialTab);
        if (aramaFromUrl) next.set('arama', aramaFromUrl);
        return next;
      }, { replace: true });
    }
  }, [tabFromUrl, initialTab, aramaFromUrl, setSearchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput.trim()) next.set('arama', searchInput.trim());
        else next.delete('arama');
        return next;
      }, { replace: true });
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [searchInput, setSearchParams]);

  const handleTabChange = (tabId) => {
    setFilters((prev) => ({ ...prev, tab: tabId }));
    setCurrentPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    }, { replace: true });
  };

  const handleSearchChange = (e) => {
    setSearchInput(sanitizeSearchInput(e.target.value, { trimEdges: false }));
  };

  const handleViewDetail = async (siparis) => {
    const id = getSiparisId(siparis);
    if (!id) {
      window.showNotification?.('error', 'Sipariş ID alınamadı');
      return;
    }
    try {
      const response = await fetchSiparisDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedSiparis(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleIptal = async (siparis) => {
    const ok = await confirm({
      title: 'Siparişi iptal et',
      message: `${siparis.siparisNumarasi} siparişini iptal etmek istediğinize emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, iptal et',
    });
    if (!ok) return;
    const id = getSiparisId(siparis);
    if (!id) return;
    setActionLoading(id);
    try {
      const response = await iptalSiparis(id);
      if (response?.basarili) {
        window.showNotification?.('success', 'Sipariş iptal edildi');
        loadSiparisler();
        refreshSekmeSayilari();
        setDetailModalOpen(false);
      } else {
        window.showNotification?.('error', response?.mesaj || 'İptal başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İptal başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  const openKargoModal = (siparis) => {
    setKargoModal({ open: true, siparis });
    setKargoForm({ kargoFirmasi: '', kargoTakipNo: '' });
  };

  const handleKargoSubmit = async () => {
    if (!kargoModal.siparis) return;
    if (!kargoForm.kargoFirmasi?.trim() || !kargoForm.kargoTakipNo?.trim()) {
      window.showNotification?.('error', 'Kargo firması ve takip numarası zorunludur');
      return;
    }
    const id = getSiparisId(kargoModal.siparis);
    if (!id) return;
    setActionLoading(id);
    try {
      const response = await siparisKargoGir(id, kargoForm);
      if (response?.basarili) {
        window.showNotification?.('success', 'Kargo bilgisi kaydedildi');
        setKargoModal({ open: false, siparis: null });
        loadSiparisler();
        refreshSekmeSayilari();
        setDetailModalOpen(false);
      } else {
        window.showNotification?.('error', response?.mesaj || 'Kayıt başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Kayıt başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  const openOnayModal = (siparis) => {
    setOnayModal({ open: true, siparis });
  };

  const handleOnaySuccess = () => {
    window.showNotification?.('success', 'Sipariş onaylandı');
    setOnayModal({ open: false, siparis: null });
    loadSiparisler();
    refreshSekmeSayilari();
    setDetailModalOpen(false);
  };

  const openFaturaModal = (siparis) => {
    setFaturaModal({ open: true, siparis });
    setFaturaBilgisi('');
  };

  const handleFaturaSubmit = async () => {
    if (!faturaModal.siparis) return;
    if (!faturaBilgisi?.trim()) {
      window.showNotification?.(
        'error',
        faturaModal.siparis.konsinye ? 'Sevk irsaliyesi kodu zorunludur' : 'Fatura bilgisi zorunludur',
      );
      return;
    }
    const id = getSiparisId(faturaModal.siparis);
    if (!id) return;
    setActionLoading(id);
    try {
      const response = await siparisFaturaGir(id, faturaBilgisi.trim());
      if (response?.basarili) {
        window.showNotification?.(
          'success',
          faturaModal.siparis.konsinye ? 'Sevk irsaliyesi kaydedildi' : 'Fatura bilgisi kaydedildi',
        );
        setFaturaModal({ open: false, siparis: null });
        loadSiparisler();
        refreshSekmeSayilari();
      } else {
        window.showNotification?.('error', response?.mesaj || 'Kayıt başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Kayıt başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <main className="main-content">
      <div className="content-header">
        <h1>{cargoOnly ? 'Sipariş Kargoları' : 'Sipariş Takip Sistemi'}</h1>
      </div>

      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <div className="search-box" style={{ flex: 1, maxWidth: '350px' }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Sipariş no, teknik servis veya tarih ile ara..."
            value={searchInput}
            onChange={handleSearchChange}
            maxLength={100}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {TABS.map((tab) => {
          const n = sekmeSayilari?.[tab.id];
          return (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn siparis-tab-with-badge ${filters.tab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span>{tab.label}</span>
              {typeof n === 'number' ? <span className="siparis-tab-badge">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Yükleniyor...
          </div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
        ) : siparisler.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Sipariş bulunamadı
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Sipariş No</th>
                    <th>Teknik Servis</th>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th>Servis Kaydı</th>
                    {filters.tab === 'kargoda' && (
                      <>
                        <th>Kargo Firması</th>
                        <th>Takip No</th>
                      </>
                    )}
                    {!cargoOnly && <th style={{ textAlign: 'right' }}>Fiyat</th>}
                    <th className="islemler-sutunu">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {siparisler.map((s, idx) => (
                    <tr key={getSiparisId(s) || idx}>
                      <td style={{ fontWeight: 500 }}>{s.siparisNumarasi}</td>
                      <td>{s.teknikServisAdi || '—'}</td>
                      <td>{formatApiDate(s.olusturmaTarihi)}</td>
                      <td>{getSiparisTipLabel(s)}</td>
                      <td>
                        {s.servisKaydiId ? (
                          <Link
                            to={`/service-records/${s.servisKaydiId}/update`}
                            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
                          >
                            {s.servisKaydiNumarasi || s.servisKaydiId}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      {filters.tab === 'kargoda' && (
                        <>
                          <td>{s.kargoFirmasi ?? '—'}</td>
                          <td>{s.kargoTakipNo ?? '—'}</td>
                        </>
                      )}
                      {!cargoOnly && (
                        <td style={{ textAlign: 'right' }}>
                          {formatSiparisListeFiyat(s, formatCurrency)}
                        </td>
                      )}
                      <td className="table-actions islemler-sutunu">
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => handleViewDetail(s)}
                          title="Detay"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {!cargoOnly && filters.tab === 'onay-bekleyen' && (
                          <>
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => openOnayModal(s)}
                              title="Onayla"
                              disabled={actionLoading === getSiparisId(s)}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              type="button"
                              className="category-action-btn delete-btn"
                              onClick={() => handleIptal(s)}
                              title="İptal"
                              disabled={actionLoading === getSiparisId(s)}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                        {!cargoOnly && filters.tab === 'fatura-bekleyen' && (
                          <>
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => openFaturaModal(s)}
                              title={s.konsinye ? 'Sevk irsaliyesi gir' : 'Fatura Gir'}
                              disabled={actionLoading === getSiparisId(s)}
                            >
                              <i className="fas fa-file-invoice"></i>
                            </button>
                            {s.durum !== 'FaturaBekleyen' && (
                              <button
                                type="button"
                                className="category-action-btn delete-btn"
                                onClick={() => handleIptal(s)}
                                title="İptal"
                                disabled={actionLoading === getSiparisId(s)}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </>
                        )}
                        {filters.tab === 'kargo-bekleyen' && (
                          <>
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => openKargoModal(s)}
                              title="Kargo Gir"
                              disabled={actionLoading === getSiparisId(s)}
                            >
                              <i className="fas fa-truck"></i>
                            </button>
                            {!cargoOnly && (
                              <button
                                type="button"
                                className="category-action-btn delete-btn"
                                onClick={() => handleIptal(s)}
                                title="İptal"
                                disabled={actionLoading === getSiparisId(s)}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </>
                        )}
                        {!cargoOnly && filters.tab === 'kargoda' && (
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => handleIptal(s)}
                            title="İptal"
                            disabled={actionLoading === getSiparisId(s)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                        {!cargoOnly && filters.tab === 'tamamlanan' && (
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => handleIptal(s)}
                            title="İptal"
                            disabled={actionLoading === getSiparisId(s)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {detailModalOpen && selectedSiparis && (
        <SiparisDetayModal
          siparis={selectedSiparis}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedSiparis(null);
          }}
          onRefresh={loadSiparisler}
          isAdmin={!cargoOnly}
          showFiyat={!cargoOnly}
        />
      )}

      {kargoModal.open && kargoModal.siparis && (
        <div className="modal-overlay" onClick={() => setKargoModal({ open: false, siparis: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Kargo Bilgisi - {kargoModal.siparis.siparisNumarasi}</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setKargoModal({ open: false, siparis: null })}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '12px' }}>
                <KargoFirmasiField
                  idPrefix="siparis-kargo"
                  value={kargoForm.kargoFirmasi}
                  onChange={(ad) => setKargoForm((prev) => ({ ...prev, kargoFirmasi: ad }))}
                  disabled={!!actionLoading}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Kargo Takip No</label>
                <input
                  type="text"
                  value={kargoForm.kargoTakipNo}
                  onChange={(e) => setKargoForm((prev) => ({ ...prev, kargoTakipNo: e.target.value }))}
                  placeholder="Takip numarası"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setKargoModal({ open: false, siparis: null })}>
                İptal
              </button>
              <button type="button" className="btn btn-primary" onClick={handleKargoSubmit} disabled={actionLoading}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {onayModal.open && onayModal.siparis && (
        <SiparisOnaylaModal
          siparis={onayModal.siparis}
          getSiparisId={getSiparisId}
          onClose={() => setOnayModal({ open: false, siparis: null })}
          onSuccess={handleOnaySuccess}
        />
      )}

      {faturaModal.open && faturaModal.siparis && (
        <div className="modal-overlay" onClick={() => setFaturaModal({ open: false, siparis: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>
                {faturaModal.siparis.konsinye ? 'Sevk İrsaliyesi' : 'Fatura Bilgisi'} — {faturaModal.siparis.siparisNumarasi}
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setFaturaModal({ open: false, siparis: null })}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {faturaModal.siparis.konsinye ? 'Sevk irsaliyesi no *' : 'Fatura Bilgisi *'}
              </label>
              <input
                type="text"
                value={faturaBilgisi}
                onChange={(e) => setFaturaBilgisi(e.target.value)}
                placeholder={faturaModal.siparis.konsinye ? 'Sevk irsaliyesi kodu' : 'Fatura no veya bilgi'}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: '8px' }}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setFaturaModal({ open: false, siparis: null })}>
                İptal
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFaturaSubmit}
                disabled={actionLoading || !faturaBilgisi?.trim()}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default SiparisTakipPage;
